import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { useToast } from '@lawbee/ui';
import { useCallback, useState } from 'react';
import { errorMessage } from './api';

/** A path param from the current route (`$id` → `useParam('id')`). */
export function useParam(name: string): string {
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  return params[name] ?? '';
}

/** A search param from the current URL, as a string (TanStack parses numbers/booleans as JSON). */
export function useSearchParam(name: string): string | undefined {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const v = search[name];
  if (v === undefined || v === null) return undefined;
  return typeof v === 'string' ? v : String(v);
}

/**
 * Mutation with toasts and cache invalidation. `success` is the toast title (omit for no toast).
 * Errors always toast with the API message.
 */
export function useApiMutation<TVars, TData = unknown>(
  fn: (vars: TVars) => Promise<TData>,
  opts: { success?: string | ((data: TData) => string); invalidate?: QueryKey[]; onSuccess?: (data: TData, vars: TVars) => void; errorTitle?: string } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (data, vars) => {
      if (opts.invalidate) await Promise.all(opts.invalidate.map((key) => qc.invalidateQueries({ queryKey: key })));
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(data) : opts.success);
      opts.onSuccess?.(data, vars);
    },
    onError: (err) => {
      toast.error(opts.errorTitle ?? 'Could not save', errorMessage(err));
    },
  });
}

/** Copy text to the clipboard with a toast. */
export function useCopy() {
  const toast = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const copy = useCallback(
    async (text: string, label = 'Copied') => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(text);
        toast.success(label);
        setTimeout(() => setCopied(null), 1500);
      } catch {
        toast.error('Could not copy', 'Select the text and copy it manually.');
      }
    },
    [toast],
  );
  return { copy, copied };
}

/** Format minutes after midnight as HH:MM. */
export function minutesToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function timeToMinutes(value: string): number {
  const [h, m] = value.split(':').map((x) => Number(x));
  return (h ?? 0) * 60 + (m ?? 0);
}

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/** Dollars string ("349.50") to integer cents. */
export function dollarsToCents(value: string): number {
  const n = Number(value.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const s = value.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Today in a timezone as YYYY-MM-DD. */
export function todayIn(timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function formatInTz(iso: string, timezone: string, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, ...opts }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('en-CA', opts).format(new Date(iso));
  }
}

export const PROVINCES: { value: string; label: string }[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

export const TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/St_Johns', label: 'Newfoundland (St. John’s)' },
  { value: 'America/Halifax', label: 'Atlantic (Halifax)' },
  { value: 'America/Moncton', label: 'Atlantic (Moncton)' },
  { value: 'America/Toronto', label: 'Eastern (Toronto)' },
  { value: 'America/Winnipeg', label: 'Central (Winnipeg)' },
  { value: 'America/Regina', label: 'Saskatchewan (Regina)' },
  { value: 'America/Edmonton', label: 'Mountain (Edmonton)' },
  { value: 'America/Vancouver', label: 'Pacific (Vancouver)' },
  { value: 'America/Whitehorse', label: 'Yukon (Whitehorse)' },
];
